import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useInView } from '../../../hooks/useInView';

const testimonials = [
  {
    quote: "Immanuel Production Group elevated our brand presence to an entirely different level. The cinematic quality of their broadcast production generated immediate, measurable positive responses from our audience and executive stakeholders. They are a true creative and strategic partner.",
    name: 'Marcus T.',
    role: 'Marketing Director',
    company: 'National Corporate Brand',
    rating: 5,
    avatar: 'https://public.readdy.ai/ai/img_res/cf1ec9bfcf466ac1c37d62cf7ded6e8a.jpg',
  },
  {
    quote: "From concept to final delivery, the IPG team was professional, deeply communicative, and genuinely invested in making our campaign a success. The final broadcast-quality production exceeded every expectation we had set at the beginning of the engagement.",
    name: 'Sarah L.',
    role: 'Chief Executive Officer',
    company: 'Nonprofit Organization, Central Florida',
    rating: 5,
    avatar: 'https://readdy.ai/api/search-image?query=professional%20executive%20headshot%20latina%20woman%20business%20leader%20dark%20studio%20background%20warm%20confident%20smile%20corporate%20photography%20high%20end%20portrait&width=200&height=200&seq=avt3_002&orientation=squarish',
  },
  {
    quote: "We've worked with many production companies over the years. None have matched the strategic thinking and cinematic execution that Immanuel Production Group brings. They are our exclusive production partner for every major national campaign.",
    name: 'David R.',
    role: 'Brand Manager',
    company: 'National Retail Organization',
    rating: 5,
    avatar: 'https://readdy.ai/api/search-image?query=professional%20executive%20headshot%20white%20man%20business%20professional%20dark%20studio%20background%20confident%20poised%20corporate%20photography%20high%20end%20portrait&width=200&height=200&seq=avt3_003&orientation=squarish',
  },
  {
    quote: "The bilingual production capability was exactly what we needed for our Latin market campaign. IPG delivered a visually stunning broadcast-quality production that resonated deeply with our target audience across multiple platforms. Exceptional execution at every level.",
    name: 'Maria G.',
    role: 'VP of Communications',
    company: 'Florida Healthcare Group',
    rating: 5,
    avatar: 'https://readdy.ai/api/search-image?query=professional%20executive%20headshot%20hispanic%20woman%20executive%20dark%20studio%20background%20confident%20leadership%20corporate%20photography%20high%20end%20studio%20portrait&width=200&height=200&seq=avt3_004&orientation=squarish',
  },
];

export default function Testimonials() {
  const { ref, inView } = useInView();
  const [active, setActive] = useState(0);

  const t = testimonials[active];

  return (
    <section
      ref={ref as React.RefObject<HTMLDivElement>}
      className="relative overflow-hidden"
      id="testimonials"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=abstract%20dark%20luxury%20cinematic%20texture%20deep%20rich%20black%20surface%20subtle%20light%20rays%20golden%20particles%20film%20grain%20atmospheric%20premium%20high%20end%20brand%20background%20near%20black%20dark&width=1920&height=1000&seq=testi3_bg&orientation=landscape"
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#080808]/92" />
        <div className="absolute inset-0 bg-grain opacity-25 pointer-events-none" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-8 lg:px-20 py-36">
        {/* Header */}
        <div className={`flex flex-col md:flex-row md:items-end justify-between gap-8 mb-20 transition-all duration-800 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div>
            <p className="font-sans text-[0.58rem] tracking-[0.45em] uppercase text-gold mb-6 font-medium">Client Results</p>
            <h2 className="font-serif font-bold text-white leading-tight" style={{ fontSize: 'clamp(2.8rem, 5vw, 5rem)', letterSpacing: '-0.025em' }}>
              What Our Clients<br />
              <em className="not-italic text-gold">Say About Us</em>
            </h2>
          </div>
          {/* Selector dots */}
          <div className="flex items-center gap-3">
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                className={`cursor-pointer transition-all duration-400 ${active === i ? 'bg-gold w-10 h-0.5' : 'bg-white/20 w-5 h-0.5 hover:bg-white/40'}`}
                aria-label={`Testimonial ${i + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Main testimonial — full width dramatic */}
        <div className={`transition-all duration-800 delay-100 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="relative mb-8">
            {/* Large quote mark */}
            <i className="ri-double-quotes-l absolute -top-4 -left-4 text-gold/8" style={{ fontSize: '10rem', lineHeight: 1 }} />

            <div className="relative p-10 lg:p-16 border border-gold/15 bg-white/[0.015] overflow-hidden">
              {/* Gold top line */}
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-gold/60 via-gold/20 to-transparent" />

              {/* Star rating */}
              <div className="flex gap-1 mb-8">
                {Array.from({ length: t.rating }).map((_, i) => (
                  <i key={i} className="ri-star-fill text-gold text-base" />
                ))}
              </div>

              {/* The quote — huge typography */}
              <blockquote
                className="font-serif text-white/80 leading-[1.55] italic mb-12"
                style={{ fontSize: 'clamp(1.3rem, 2.5vw, 2rem)' }}
              >
                &ldquo;{t.quote}&rdquo;
              </blockquote>

              {/* Author */}
              <div className="flex items-center gap-6">
                <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-gold/30 flex-shrink-0">
                  <img src={t.avatar} alt={t.name} className="w-full h-full object-cover object-top" />
                </div>
                <div>
                  <p className="font-sans text-base font-semibold text-white">{t.name}</p>
                  <p className="font-sans text-sm text-gold mt-0.5">{t.role}</p>
                  <p className="font-sans text-xs text-white/30 mt-0.5">{t.company}</p>
                </div>
                <div className="ml-auto hidden lg:flex items-center gap-3">
                  <div className="w-16 h-px bg-gold/20" />
                  <span className="font-sans text-[0.58rem] tracking-[0.3em] uppercase text-white/20">Verified Client</span>
                </div>
              </div>
            </div>
          </div>

          {/* Thumbnail row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {testimonials.map((item, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                className={`p-5 border text-left cursor-pointer transition-all duration-300 group ${
                  active === i ? 'border-gold/40 bg-gold/6' : 'border-white/5 bg-white/[0.012] hover:border-white/12 hover:bg-white/[0.025]'
                }`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-full overflow-hidden border border-white/15 flex-shrink-0">
                    <img src={item.avatar} alt={item.name} className="w-full h-full object-cover object-top" />
                  </div>
                  <div>
                    <p className="font-sans text-[0.65rem] font-semibold text-white whitespace-nowrap">{item.name}</p>
                    <p className="font-sans text-[0.58rem] text-gold/55 whitespace-nowrap">{item.role}</p>
                  </div>
                </div>
                <p className="font-sans text-[0.62rem] text-white/28 line-clamp-2 leading-relaxed group-hover:text-white/45 transition-colors duration-300">
                  {item.quote.substring(0, 75)}...
                </p>
              </button>
            ))}
          </div>
        </div>

        {/* Post CTA */}
        <div className={`mt-16 flex flex-col sm:flex-row items-center justify-between gap-6 pt-12 border-t border-white/5 transition-all duration-800 delay-300 ${inView ? 'opacity-100' : 'opacity-0'}`}>
          <div>
            <p className="font-serif text-xl text-white/55 italic mb-1">&ldquo;Join 500+ brands who trust Immanuel Production Group.&rdquo;</p>
            <p className="font-sans text-xs text-white/25 tracking-wide">20+ Years &nbsp;&bull;&nbsp; Broadcast-Level Quality &nbsp;&bull;&nbsp; Central Florida</p>
          </div>
          <Link
            to="/contact"
            className="whitespace-nowrap font-sans text-[0.68rem] font-semibold tracking-[0.2em] uppercase px-10 py-4 bg-gold text-[#020202] hover:bg-white transition-colors duration-300 cursor-pointer flex-shrink-0"
          >
            Start Your Project
          </Link>
        </div>
      </div>
    </section>
  );
}
